import { useEffect, useState } from 'react';

/**
 * Network Manager for detecting online/offline status
 * Handles automatic sync triggers when connection is restored
 */
export class NetworkManager {
  private static instance: NetworkManager;
  private isOnline = navigator.onLine;
  private listeners: Array<(isOnline: boolean) => void> = [];
  private syncCallbacks: Array<() => Promise<void>> = [];

  private constructor() {
    this.setupEventListeners();
  }

  static getInstance(): NetworkManager {
    if (!NetworkManager.instance) {
      NetworkManager.instance = new NetworkManager();
    }
    return NetworkManager.instance;
  }

  private setupEventListeners(): void {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    // Additional connectivity checks for more reliable detection
    this.startConnectivityPolling();
  }

  private handleOnline(): void {
    console.log('🌐 Network: Back online');
    this.isOnline = true;
    this.notifyListeners();
    this.triggerSync();
  }

  private handleOffline(): void {
    console.log('📴 Network: Gone offline');
    this.isOnline = false;
    this.notifyListeners();
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.isOnline);
      } catch (error) {
        console.error('Error in network listener:', error);
      }
    });
  }

  private async triggerSync(): Promise<void> {
    if (!this.isOnline) return;

    console.log('🔄 Triggering automatic sync...');
    
    for (const callback of this.syncCallbacks) {
      try {
        await callback();
      } catch (error) {
        console.error('Error during automatic sync:', error);
      }
    }
  }

  private startConnectivityPolling(): void {
    // Poll every 30 seconds to check real connectivity
    setInterval(() => {
      this.checkConnectivity();
    }, 30000);
  }

  private async checkConnectivity(): Promise<void> {
    try {
      // Try to fetch a small resource to verify real connectivity
      const response = await fetch('/ping', { 
        method: 'HEAD',
        cache: 'no-cache',
        timeout: 5000 
      } as any);
      
      const wasOnline = this.isOnline;
      this.isOnline = response.ok;
      
      if (wasOnline !== this.isOnline) {
        this.notifyListeners();
        if (this.isOnline) {
          this.triggerSync();
        }
      }
    } catch (error) {
      const wasOnline = this.isOnline;
      this.isOnline = false;
      
      if (wasOnline !== this.isOnline) {
        this.notifyListeners();
      }
    }
  }

  /**
   * Public API
   */

  getConnectionStatus(): boolean {
    return this.isOnline;
  }

  addNetworkListener(callback: (isOnline: boolean) => void): void {
    this.listeners.push(callback);
  }

  removeNetworkListener(callback: (isOnline: boolean) => void): void {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  addSyncCallback(callback: () => Promise<void>): void {
    this.syncCallbacks.push(callback);
  }

  removeSyncCallback(callback: () => Promise<void>): void {
    this.syncCallbacks = this.syncCallbacks.filter(cb => cb !== callback);
  }

  async forceConnectivityCheck(): Promise<boolean> {
    await this.checkConnectivity();
    return this.isOnline;
  }

  // Cleanup method
  destroy(): void {
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
    this.listeners = [];
    this.syncCallbacks = [];
  }
}

/**
 * React Hook for using network status in components
 */
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkManager] = useState(() => NetworkManager.getInstance());

  useEffect(() => {
    const handleNetworkChange = (online: boolean) => {
      setIsOnline(online);
    };

    // Add listener
    networkManager.addNetworkListener(handleNetworkChange);

    // Initial status
    setIsOnline(networkManager.getConnectionStatus());

    // Cleanup
    return () => {
      networkManager.removeNetworkListener(handleNetworkChange);
    };
  }, [networkManager]);

  return {
    isOnline,
    forceCheck: () => networkManager.forceConnectivityCheck()
  };
}

// Export singleton instance
export const networkManager = NetworkManager.getInstance();